import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { FarmForm, OrchardMap } from '../../components/orchard';
import { Maximize2, CheckCircle } from 'lucide-react';
import orchardService from '../../services/orchard';
import type { GeoJSONPolygon } from '../../types/orchard';

function FarmCreate() {
  const navigate = useNavigate();
  const [polygon, setPolygon] = useState<GeoJSONPolygon | null>(null);
  const [areaFromMap, setAreaFromMap] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successId, setSuccessId] = useState<number | null>(null);
  const [focusBounds, setFocusBounds] = useState<[number, number, number, number] | null>(null);

  // Recomputed rather than reused from the submit handler, because the region
  // suggestion needs it while the boundary is being drawn, not at save time.
  const centerPoint = useMemo(
    () => (polygon ? orchardService.calculateCenterPoint(polygon.coordinates) : null),
    [polygon]
  );

  const handlePolygonCreated = useCallback(async (newPolygon: GeoJSONPolygon) => {
    setPolygon(newPolygon);
    try {
      const response = await orchardService.calculateArea(newPolygon);
      if (response.success && response.data) {
        setAreaFromMap(response.data.hectares);
      }
    } catch {
      // Area will be calculated client-side by the service fallback
      const center = orchardService.calculateCenterPoint(newPolygon.coordinates);
      setAreaFromMap(null);
      console.warn('Server area calculation failed, user can enter manually', center);
    }
  }, []);

  const handlePolygonEdited = useCallback(async (editedPolygon: GeoJSONPolygon) => {
    setPolygon(editedPolygon);
    try {
      const response = await orchardService.calculateArea(editedPolygon);
      if (response.success && response.data) {
        setAreaFromMap(response.data.hectares);
      }
    } catch {
      setAreaFromMap(null);
    }
  }, []);

  const handleSubmit = async (fieldData: {
    name: string;
    description?: string;
    location?: string;
    total_area_hectares?: number;
    growing_region_id?: number | null;
    code?: string;
    plan_rows_by_position?: boolean;
    active?: boolean;
    target_org_id?: number;
  }) => {
    if (!polygon) return;

    setIsSubmitting(true);
    setError(null);

    const centerPoint = orchardService.calculateCenterPoint(polygon.coordinates);

    try {
      const response = await orchardService.createFarm({
        name: fieldData.name,
        description: fieldData.description,
        location: fieldData.location,
        boundary_geometry: polygon,
        center_point: centerPoint,
        total_area_hectares: fieldData.total_area_hectares ?? areaFromMap ?? undefined,
        growing_region_id: fieldData.growing_region_id ?? null,
        code: fieldData.code || undefined,
        plan_rows_by_position: fieldData.plan_rows_by_position === true,
        active: fieldData.active,
        target_org_id: fieldData.target_org_id,
      });

      if (response.success && response.data) {
        setSuccessId(response.data.id);
        const qs = fieldData.target_org_id ? `?org_id=${fieldData.target_org_id}` : '';
        setTimeout(() => {
          navigate(`/orchard/farms/${response.data!.id}${qs}`);
        }, 1500);
      } else {
        setError((response as any).message || 'Failed to create farm');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddressSelected = (r: { lat: number; lon: number; bbox: [number, number, number, number] | null }) => {
    if (r.bbox) {
      setFocusBounds(r.bbox);
    } else {
      const d = 0.003;
      setFocusBounds([r.lat - d, r.lat + d, r.lon - d, r.lon + d]);
    }
  };

  if (successId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="New Farm"
          subtitle="Draw a boundary to define your farm"
          icon={<Maximize2 className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <div className="h-16 w-16 rounded-full bg-success-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-success-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">Farm Created!</h2>
              <p className="text-neutral-600">Redirecting to farm details...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="New Farm"
        subtitle="Draw a boundary on the map to define your farm"
        icon={<Maximize2 className="h-6 w-6 text-white" />}
        showBackButton
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-neutral-900 mb-4">Farm Details</h2>
              <FarmForm
                areaFromMap={areaFromMap}
                hasPolygon={!!polygon}
                centerPoint={centerPoint}
                onSubmit={handleSubmit}
                onCancel={() => navigate('/orchard')}
                isSubmitting={isSubmitting}
                onAddressSelected={handleAddressSelected}
              />
            </CardContent>
          </Card>

          {/* Map */}
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <p className="text-sm text-amber-800 font-medium">
                Click the map to add boundary points. Use ✓ Finish (top-right) when done — 3 points minimum, no limit on maximum.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden border border-neutral-200 shadow-sm" style={{ height: '560px' }}>
              <OrchardMap
                drawMode="farm"
                onPolygonCreated={handlePolygonCreated}
                onPolygonEdited={handlePolygonEdited}
                focusBounds={focusBounds}
              />
            </div>
            {polygon && (
              <p className="text-sm text-success-700 font-medium">
                ✓ Farm boundary drawn{areaFromMap ? ` · ${areaFromMap.toFixed(2)} ha` : ''}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FarmCreate;
